import { FunctionComponent } from "react";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetSalesforceCompanySyncTriggerQuery,
	useUpdateSalesforceCompanySyncTriggerMutation,
} from "@/pages/Private/redux/salesforce/salesforce.api";
import {
	useGetSalesforceConversationSyncQuery,
	useGetSalesforceDealCreationTriggerQuery,
	useUpdateSalesforceConversationSyncMutation,
	useUpdateSalesforceDealCreationTriggerMutation,
} from "@/pages/Private/redux/salesforce/salesforceDeal.api";

import { SalesforceSyncTrigger } from "../schema/salesforce";
import { SalesforceDealCreationTrigger } from "../schema/salesforceDeal";
import { SalesforceResyncSection } from "./SalesforceResyncSection";

type Ts = (key: string) => string;

interface SalesforceSyncSettingsTabProps {
	isConnected: boolean;
	ts: Ts;
}

/**
 * The account-wide sync settings: when prospects sync, when an opportunity (or lead) is created
 * after a reply, whether conversations are mirrored as Tasks, and the manual resync.
 *
 * The opportunity-creation trigger is rendered **here only** — `salesforce_deal_creation_trigger`
 * is keyed by `companyId` alone, so unlike HubSpot there is no per-list counterpart.
 *
 * §3.3's "Allow duplicate records" toggle is deliberately absent: SF-DUP's backend half was
 * never built (no column, no endpoint, no `Sforce-Duplicate-Rule-Header` on the write path), so
 * the control would have nothing to persist to. A `DUPLICATES_DETECTED` rejection surfaces on
 * the sync log instead, which is the half that does exist.
 */
export const SalesforceSyncSettingsTab: FunctionComponent<SalesforceSyncSettingsTabProps> = ({
	isConnected,
	ts,
}) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: companySyncTrigger } = useGetSalesforceCompanySyncTriggerQuery(undefined, {
		skip: !isConnected,
	});
	const [updateCompanySyncTrigger] = useUpdateSalesforceCompanySyncTriggerMutation();

	const { data: dealCreationTrigger } = useGetSalesforceDealCreationTriggerQuery(undefined, {
		skip: !isConnected,
	});
	const [updateDealCreationTrigger] = useUpdateSalesforceDealCreationTriggerMutation();

	const { data: conversationSync } = useGetSalesforceConversationSyncQuery(undefined, {
		skip: !isConnected,
	});
	const [updateConversationSync] = useUpdateSalesforceConversationSyncMutation();

	const syncTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: SalesforceSyncTrigger.DISABLED, title: ts("syncTrigger.DISABLED") },
		{ id: SalesforceSyncTrigger.ON_CAMPAIGN_START, title: ts("syncTrigger.ON_CAMPAIGN_START") },
		{ id: SalesforceSyncTrigger.ON_REPLY, title: ts("syncTrigger.ON_REPLY") },
	];

	const dealCreationTriggerOptions: DropdownAutoCompleteItem[] = [
		{ id: SalesforceDealCreationTrigger.DISABLED, title: ts("dealCreationTrigger.DISABLED") },
		{ id: SalesforceDealCreationTrigger.ON_RESPONSE, title: ts("dealCreationTrigger.ON_RESPONSE") },
		{
			id: SalesforceDealCreationTrigger.ON_POSITIVE_RESPONSE,
			title: ts("dealCreationTrigger.ON_POSITIVE_RESPONSE"),
		},
	];

	const handleSyncTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateCompanySyncTrigger({ trigger: value.id as SalesforceSyncTrigger }).unwrap();
		} catch {
			handleOpenSnackbar(ts("syncTrigger.error"));
		}
	};

	const handleDealCreationTriggerChange = async (value: DropdownAutoCompleteItem) => {
		try {
			await updateDealCreationTrigger({
				trigger: value.id as SalesforceDealCreationTrigger,
				createAsLead: dealCreationTrigger?.createAsLead ?? false,
			}).unwrap();
		} catch {
			handleOpenSnackbar(ts("dealCreationTrigger.error"));
		}
	};

	const handleCreateAsLeadToggle = async () => {
		try {
			await updateDealCreationTrigger({
				trigger: dealCreationTrigger?.trigger ?? SalesforceDealCreationTrigger.DISABLED,
				createAsLead: !(dealCreationTrigger?.createAsLead ?? false),
			}).unwrap();
		} catch {
			handleOpenSnackbar(ts("dealCreationTrigger.error"));
		}
	};

	const handleConversationSyncToggle = async () => {
		try {
			await updateConversationSync({ enabled: !(conversationSync?.enabled ?? false) }).unwrap();
			handleOpenSnackbar(ts("conversationSync.success"));
		} catch {
			handleOpenSnackbar(ts("conversationSync.error"));
		}
	};

	return (
		<div className="flex w-full flex-col">
			{renderMessageSnackbar()}
			<div className="mb-3 text-sm text-text-meta">{ts("syncTrigger.description")}</div>
			<div className="w-full max-w-[400px]" data-testid="salesforce-sync-trigger">
				<Dropdown
					data={syncTriggerOptions}
					defaultValue={
						companySyncTrigger
							? {
									id: companySyncTrigger.trigger,
									title: ts(`syncTrigger.${companySyncTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleSyncTriggerChange}
					label={ts("syncTrigger.title")}
				/>
			</div>

			<div className="mt-6 mb-3 text-sm text-text-meta">
				{ts("dealCreationTrigger.description")}
			</div>
			<div className="w-full max-w-[400px]" data-testid="salesforce-deal-creation-trigger">
				<Dropdown
					data={dealCreationTriggerOptions}
					defaultValue={
						dealCreationTrigger
							? {
									id: dealCreationTrigger.trigger,
									title: ts(`dealCreationTrigger.${dealCreationTrigger.trigger}`),
								}
							: undefined
					}
					floating={true}
					handleSelect={handleDealCreationTriggerChange}
					label={ts("dealCreationTrigger.title")}
				/>
			</div>
			<div className="mt-4">
				<Checkbox
					isChecked={dealCreationTrigger?.createAsLead ?? false}
					name="createAsLead"
					onChange={handleCreateAsLeadToggle}
				>
					{ts("dealCreationTrigger.createAsLead")}
				</Checkbox>
				<div className="mt-1 text-sm text-text-meta">
					{ts("dealCreationTrigger.createAsLeadDescription")}
				</div>
			</div>

			<div className="mt-6 border-t border-border-light pt-6">
				<div className="mb-2 text-sm font-medium text-text">{ts("conversationSync.title")}</div>
				<Checkbox
					isChecked={conversationSync?.enabled ?? false}
					name="conversationSync"
					onChange={handleConversationSyncToggle}
				>
					{ts("conversationSync.label")}
				</Checkbox>
				<div className="mt-2 text-sm text-text-meta">{ts("conversationSync.description")}</div>
			</div>

			<SalesforceResyncSection />
		</div>
	);
};
